// A list of API keys or connected apps (Claude, ChatGPT), with an in-page confirmation before a revoke.
// Account shows the caller's own; the admin user drawer shows one user's
// (docs/plans/claude-connector.md §5). A row that ended (revoked or expired) stays listed for
// 30 days, greyed, with why it ended in words.
//
// Focus follows the confirmation: opening it focuses its first control (Cancel); Cancel
// returns focus to the row's Revoke button; a revoke returns it to that button if it failed,
// or, once the list shows the credential ended (its button is gone), to the section heading
// named by `focusFallbackId`.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MutationErrorLine } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { fmtDate } from '@/lib/datetime';
import {
  ACCESS_LABEL,
  credentialEnded,
  revokedReason,
  type CredentialViewer,
} from '@/lib/credentials';
import type { CredentialSummary } from '@/api/types';

export interface CredentialListProps {
  credentials: readonly CredentialSummary[];
  /** Names the list for assistive technology ("Your API keys"). */
  label: string;
  viewer: CredentialViewer;
  /** What an empty list says. */
  empty: ReactNode;
  /** Absent: the viewer may not revoke, and no control is drawn. */
  onRevoke?: (credential: CredentialSummary) => void;
  /** The credential a revoke is in flight for. */
  revokingId?: string | null;
  /** The last revoke's failure, shown under the row it was for. */
  revokeError?: { id: string; error: unknown } | null;
  /** The focusable heading (`tabIndex={-1}`) that takes focus when a revoked row's button goes. */
  focusFallbackId?: string;
}

/** Where focus goes back to once a confirmation closes. */
interface FocusReturn {
  id: string;
  after: 'cancel' | 'revoke';
}

export function CredentialList({
  credentials,
  label,
  viewer,
  empty,
  onRevoke,
  revokingId = null,
  revokeError = null,
  focusFallbackId,
}: CredentialListProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [returnFocus, setReturnFocus] = useState<FocusReturn | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!returnFocus) return;
    const { id, after } = returnFocus;
    const revokeButton = () => {
      const row = [...(listRef.current?.querySelectorAll<HTMLElement>('li[data-credential-id]') ?? [])]
        .find((li) => li.getAttribute('data-credential-id') === id);
      return row?.querySelector<HTMLButtonElement>('[data-revoke-trigger]') ?? null;
    };
    const heading = () => (focusFallbackId ? document.getElementById(focusFallbackId) : null);
    if (after === 'cancel') {
      revokeButton()?.focus();
      setReturnFocus(null);
      return;
    }
    if (revokingId === id) return;
    const failed = revokeError?.id === id;
    const row = credentials.find((credential) => credential.id === id);
    // Succeeded, but the list has not caught up: its button would vanish under the focus.
    if (!failed && row && !credentialEnded(row)) return;
    const button = failed ? revokeButton() : null;
    (button && !button.disabled ? button : heading())?.focus();
    setReturnFocus(null);
  }, [returnFocus, revokingId, revokeError, credentials, focusFallbackId]);

  if (credentials.length === 0) {
    return (
      <div className="panel muted" style={{ padding: '14px 16px', fontSize: 12.5, lineHeight: 1.5 }}>
        {empty}
      </div>
    );
  }
  const now = Date.now();
  return (
    <ul ref={listRef} className="panel" aria-label={label} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {credentials.map((credential, index) => (
        <CredentialRow
          key={credential.id}
          credential={credential}
          first={index === 0}
          ended={credentialEnded(credential, now)}
          viewer={viewer}
          confirming={confirming === credential.id}
          onAskRevoke={onRevoke ? () => setConfirming(credential.id) : undefined}
          onCancel={() => {
            setConfirming(null);
            setReturnFocus({ id: credential.id, after: 'cancel' });
          }}
          onConfirm={() => {
            setConfirming(null);
            setReturnFocus({ id: credential.id, after: 'revoke' });
            onRevoke?.(credential);
          }}
          revoking={revokingId === credential.id}
          error={revokeError?.id === credential.id ? revokeError.error : null}
        />
      ))}
    </ul>
  );
}

interface CredentialRowProps {
  credential: CredentialSummary;
  first: boolean;
  ended: boolean;
  viewer: CredentialViewer;
  confirming: boolean;
  onAskRevoke?: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  revoking: boolean;
  error: unknown;
}

function CredentialRow({
  credential,
  first,
  ended,
  viewer,
  confirming,
  onAskRevoke,
  onCancel,
  onConfirm,
  revoking,
  error,
}: CredentialRowProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);
  const grant = credential.kind === 'oauth_grant';
  const name = grant ? (credential.client_name ?? credential.name) : credential.name;
  const facts: string[] = [];
  if (grant && credential.redirect_host) facts.push(`Signs in through ${credential.redirect_host}`);
  if (!grant && credential.display_prefix) facts.push(`Starts ${credential.display_prefix}…`);
  facts.push(`${grant ? 'Connected' : 'Created'} ${fmtDate(credential.created_at)}`);
  facts.push(credential.last_used_at ? `Last used ${fmtDate(credential.last_used_at)}` : 'Never used');
  if (!ended) facts.push(`Expires ${fmtDate(credential.expires_at)}`);
  const endedWords = credential.revoked_at
    ? `${revokedReason(credential.revoke_reason, viewer)} · ${fmtDate(credential.revoked_at)}`
    : `Expired ${fmtDate(credential.expires_at)}`;
  const what = grant ? 'connection' : 'key';
  return (
    <li
      data-credential-id={credential.id}
      data-ended={ended ? 'true' : undefined}
      style={{ padding: '12px 14px', borderTop: first ? 'none' : '1px solid var(--line-2)' }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '8px 14px' }}>
        {/* Greyed by ink, never by opacity: a faded row fails the contrast check (axe). */}
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px' }}>
            <span style={{
              fontWeight: ended ? 500 : 650, fontSize: 13, overflowWrap: 'anywhere',
              color: ended ? 'var(--ink-2)' : 'var(--ink)',
              textDecoration: credential.revoked_at ? 'line-through' : undefined,
            }}>{name}</span>
            <span className="tag">{ACCESS_LABEL[credential.scope]}</span>
            {ended && <span className="tag">{credential.revoked_at ? 'Revoked' : 'Expired'}</span>}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>
            {facts.join(' · ')}
          </div>
          {ended && (
            <div style={{ fontSize: 11.5, marginTop: 2, color: 'var(--ink-2)' }}>{endedWords}</div>
          )}
        </div>
        {!ended && onAskRevoke && !confirming && (
          <button
            type="button"
            className="btn btn-sm"
            style={{ color: 'var(--bad)' }}
            disabled={revoking}
            onClick={onAskRevoke}
            data-revoke-trigger=""
            aria-label={`Revoke ${name}`}
          >
            {Icons.trash} {revoking ? 'Revoking…' : 'Revoke'}
          </button>
        )}
      </div>
      {confirming && (
        <div
          role="group"
          aria-label={`Confirm revoking ${name}`}
          style={{
            marginTop: 10,
            padding: '10px 12px',
            background: 'color-mix(in oklch, var(--bad) 8%, transparent)',
            border: '1px solid color-mix(in oklch, var(--bad) 35%, var(--line))',
            borderLeft: '3px solid var(--bad)',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 650 }}>Revoke this {what}?</div>
          <div style={{ color: 'var(--ink-1)', marginTop: 2 }}>
            {grant
              ? `${name} loses access at once. It can be connected again from ${name}.`
              : 'Anything using it stops working at once. A revoked key can’t be turned back on.'}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
            <button ref={cancelRef} type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="btn btn-sm"
              style={{ color: 'var(--bad)' }}
              onClick={onConfirm}
            >
              Revoke {what}
            </button>
          </div>
        </div>
      )}
      {error ? (
        <div style={{ marginTop: 8 }}>
          <MutationErrorLine error={error} />
        </div>
      ) : null}
    </li>
  );
}
